import { useEffect, useState } from "react";

import { InternalButton } from "@sharedComponents/buttons";
import Multimedia from "@sharedComponents/multimedia";

import EditOrderModal from "@components/store/confirmation/edit-order-modal";
import CancelOrderModal from "@components/store/confirmation/cancel-order-modal";

import { getRemainingOrderExpiryTime } from "@stadium/helpers/order-utility";
import { removeQueryParams } from "@stadium/helpers/routes";

import desktopStore from "@assets/images/svg/confirmation-image.svg";

import styles from "./main.module.scss";
import useRouter from "@stadium/hooks/lib/use-custom-route";

const ConfirmationBanner = ({
  orderNumber,
  isVerified,
  orderCountryId,
  orderCompletedAt,
  orderAddressId,
  fetchOrder,
  editableAfterCheckout,
  isReOrderableOrder,
}) => {
  const router = useRouter();
  const { query } = router;

  const { openEditOrderModal: isEditOrderModalOpen } = query;
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);

  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);

  const [isEditable, setIsEditable] = useState(false);

  const openEditOrderModal = () => setShowEditOrderModal(true);
  const closeEditOrderModal = () => setShowEditOrderModal(false);

  const openCancelOrderModal = () => setShowCancelOrderModal(true);
  const closeCancelOrderModal = () => setShowCancelOrderModal(false);

  useEffect(() => {
    if (isEditable && isEditOrderModalOpen === "true") {
      openEditOrderModal();
      removeQueryParams({ queryKeys: ["openEditOrderModal"], router });
    }
  }, [isEditable, isEditOrderModalOpen, router]);

  useEffect(() => {
    const remainingTime = getRemainingOrderExpiryTime(orderCompletedAt);
    if (editableAfterCheckout && orderCompletedAt && remainingTime > 0) {
      setIsEditable(true);
      const calculateTime = setTimeout(() => {
        setIsEditable(false);
      }, remainingTime * 1000);
      return () => clearTimeout(calculateTime);
    } else {
      isEditOrderModalOpen === "true" &&
        removeQueryParams({ queryKeys: ["openEditOrderModal"], router });
    }
  }, [orderCompletedAt]);

  const renderEditButton = () => (
    <InternalButton
      onClick={openEditOrderModal}
      styleType="link"
      className={styles.downloadReceipt}
    >
      Edit Order
    </InternalButton>
  );

  const renderEditOption = () =>
    isEditable && <>You can make some edits within 1 hour of ordering. {renderEditButton()}</>;

  return (
    <div className={styles.orderConfirmRow}>
      <div className={styles.orderConfirmColImage}>
        <Multimedia
          alt="desktop store image"
          src={desktopStore}
          type="image"
          height={"88"}
          width={"96"}
        />
      </div>
      <div className={styles.orderConfirmColLeft}>
        <div className={styles.confirmHeadContainer}>
          <p className={styles.odConfirmHead}>{isVerified ? "Hooray!" : "About Your Order"}</p>
        </div>
        <p className={styles.orderConfirmationMessage}>
          {isVerified ? (
            <>
              Your order <strong className={styles.strong}>#{orderNumber}</strong> has been
              confirmed. {renderEditOption()}
            </>
          ) : (
            <>
              Your order <strong className={styles.strong}>#{orderNumber}</strong> will ship once
              approved by the sender. {renderEditOption()}
            </>
          )}
        </p>
        {isEditable && showEditOrderModal && (
          <EditOrderModal
            orderNumber={orderNumber}
            orderCountryId={orderCountryId}
            closeModal={closeEditOrderModal}
            openCancelOrderModal={openCancelOrderModal}
            postEditOrderSuccess={fetchOrder}
            orderCompletedAt={orderCompletedAt}
            orderAddressId={orderAddressId}
          />
        )}
        {isEditable && showCancelOrderModal && (
          <CancelOrderModal
            orderCountryId={orderCountryId}
            postCancelOrderSuccess={fetchOrder}
            orderNumber={orderNumber}
            closeModal={closeCancelOrderModal}
            isReOrderableOrder={isReOrderableOrder}
          />
        )}
      </div>
    </div>
  );
};

export default ConfirmationBanner;
